import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flame, Gem, BookOpen, CalendarCheck, Globe, MessageSquare, Play, Sparkles, Users, Brain } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/contexts/GamificationContext";
import { Deck } from "@/types/linguadeck";
import RankProgressWidget from "./RankProgressWidget";
import { fetchDueCardsCount } from "@/lib/db";
import { supabase } from "@/lib/supabase";
import quotesData from "@/data/quotes.json";

interface HomeDashboardProps {
  decks: Deck[];
  onSelectDeck: (id: string) => void;
  onNavTo: (view: "flashcards" | "library") => void;
  onStartDueReview?: () => void;
  onStartSmartReview?: () => void;
}

export default function HomeDashboard({
  decks,
  onSelectDeck,
  onNavTo,
  onStartDueReview,
  onStartSmartReview,
}: HomeDashboardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, isAdminMode } = useAuth();
  const { crystals, xp, currentStreak: streak, studyHistory } = useGamification();
  const [dueCount, setDueCount] = useState(0);
  const { data: totalUsers = 0 } = useQuery({
    queryKey: ["admin", "totalUsers"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_total_users");
      if (error) throw error;
      return typeof data === "number" ? data : 0;
    },
    enabled: isAdminMode === true,
    refetchOnMount: "always",
  });
  const { data: totalAiSupportLogs = 0 } = useQuery({
    queryKey: ["admin", "aiSupportLogsCount"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("ai_support_logs")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
    enabled: isAdminMode === true,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!user?.id) return;
    fetchDueCardsCount(user.id).then(setDueCount).catch(() => setDueCount(0));
  }, [user?.id, decks]);

  const displayName =
    profile?.username ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    t("dashboard.guestName");

  const recentDecks = decks.slice(0, 3);
  const today = new Date().toISOString().slice(0, 10);
  const learnedToday = studyHistory?.includes(today) ? 1 : 0;

  const quotes = useMemo(() => {
    const lang = (i18n.language || "en").split("-")[0].toLowerCase();
    const langKey = lang === "pt" ? "pt-BR" : lang;
    const arr = quotesData[langKey as keyof typeof quotesData];
    if (Array.isArray(arr)) return arr as string[];
    return (quotesData.en || []) as string[];
  }, [i18n.language]);

  const [quoteIndex, setQuoteIndex] = useState(0);

  useEffect(() => {
    if (!Array.isArray(quotes) || quotes.length === 0) return;

    if (import.meta.env?.DEV) {
      console.log("Fetched quotes count:", quotes.length);
    }

    const storageKey = `studyDo_unseenQuotes_${i18n.language}`;
    let unseenIndices: number[] = [];
    try {
      const stored = localStorage.getItem(storageKey);
      unseenIndices = stored ? JSON.parse(stored) : [];
    } catch {
      unseenIndices = [];
    }

    const needsRebuild =
      !Array.isArray(unseenIndices) ||
      unseenIndices.length === 0 ||
      unseenIndices.some((i) => i >= quotes.length);

    if (needsRebuild) {
      unseenIndices = Array.from({ length: quotes.length }, (_, i) => i);
      for (let i = unseenIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [unseenIndices[i], unseenIndices[j]] = [unseenIndices[j], unseenIndices[i]];
      }
    }

    const nextIndex = unseenIndices.pop() ?? 0;
    localStorage.setItem(storageKey, JSON.stringify(unseenIndices));
    setQuoteIndex(nextIndex);
  }, [i18n.language, quotes]);

  const handleContinue = (deck: Deck) => {
    if (decks.some((d) => d.id === deck.id)) {
      if (deck.type === "tests") {
        navigate(`/test/${deck.id}`);
      } else {
        onSelectDeck(deck.id);
      }
    } else {
      navigate("/library");
    }
  };

  const statCardClass =
    "bg-[hsl(var(--card-bg))] rounded-2xl border border-[hsl(var(--border))] shadow-sm p-6 flex flex-col gap-2 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer";

  const cardStyle = {
    background: "hsl(var(--card-bg))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "1.5rem",
    padding: "24px 24px 24px",
    gap: "16px",
    cursor: "pointer",
    position: "relative" as const,
    overflow: "hidden" as const,
    display: "flex" as const,
    flexDirection: "column" as const,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    transition: "border-color .2s, box-shadow .2s, transform .2s",
  };

  return (
    <div className="max-w-[900px] md:max-w-none md:w-full">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h2
          className="neon-flicker font-sans font-bold tracking-tight"
          style={{
            fontSize: 28,
            marginBottom: 8,
            color: "hsl(var(--foreground))",
          }}
        >
          {t("dashboard.greeting", { name: displayName })}
        </h2>
        <p
          className="text-slate-500 italic flex items-center gap-2"
          style={{
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <Sparkles className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" aria-hidden />
          {quotes.length > 0 ? (quotes[quoteIndex] ?? quotes[0]) : t("dashboard.subtitle")}
        </p>
      </div>

      {/* Quick Stats Row — Mobile: single compact 2x2 card */}
      <div className="md:hidden mb-6">
        <div className="bg-[hsl(var(--card-bg))] rounded-2xl border border-[hsl(var(--border))] shadow-sm p-4">
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/gamification?tab=streaks"
              className="flex flex-col gap-0.5 min-h-[52px] no-underline text-inherit"
            >
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="text-xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--accent3))" }}>
                  {streak}
                </span>
              </div>
              <span className="text-[11px] font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))]">
                {t("dashboard.streakMode")}
              </span>
            </Link>
            <Link
              to="/gamification?tab=shop"
              className="flex flex-col gap-0.5 min-h-[52px] no-underline text-inherit"
            >
              <div className="flex items-center gap-2">
                <Gem className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--accent2))" }}>
                  {crystals.toLocaleString()}
                </span>
              </div>
              <span className="text-[11px] font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))]">
                {t("dashboard.crystals")}
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="flex flex-col gap-0.5 min-h-[52px] no-underline text-inherit"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]">
                  {learnedToday}
                </span>
              </div>
              <span className="text-[11px] font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))]">
                {t("dashboard.learnedToday")}
              </span>
            </Link>
            {onStartDueReview ? (
              <div
                role={dueCount > 0 ? "button" : undefined}
                tabIndex={dueCount > 0 ? 0 : undefined}
                onClick={dueCount > 0 ? onStartDueReview : undefined}
                onKeyDown={
                  dueCount > 0
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onStartDueReview();
                        }
                      }
                    : undefined
                }
                className={`flex flex-col gap-0.5 min-h-[52px] ${dueCount > 0 ? "cursor-pointer" : "opacity-70 cursor-default"}`}
              >
                <div className="flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4 text-purple-500 shrink-0" />
                  <span
                    className="text-xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]"
                    style={dueCount > 0 ? { color: "hsl(var(--accent2))" } : undefined}
                  >
                    {dueCount}
                  </span>
                </div>
                <span className="text-[11px] font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))]">
                  {t("dashboard.reviewToday")}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Quick Stats Row — Desktop: original cards */}
      <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link
          to="/gamification?tab=streaks"
          className={`${statCardClass} no-underline text-inherit`}
        >
          <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--accent3))" }}>
            {streak}
          </div>
          <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
            <Flame className="w-4 h-4 text-orange-500 shrink-0" />
            {t("dashboard.streakMode")}
          </div>
        </Link>
        <Link
          to="/gamification?tab=shop"
          className={`${statCardClass} no-underline text-inherit`}
        >
          <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--accent2))" }}>
            {crystals.toLocaleString()}
          </div>
          <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
            <Gem className="w-4 h-4 text-sky-500 shrink-0" />
            {t("dashboard.crystals")}
          </div>
        </Link>
        <Link
          to="/dashboard"
          className={`${statCardClass} no-underline text-inherit`}
        >
          <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]">
            {learnedToday}
          </div>
          <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-emerald-600 shrink-0" />
            {t("dashboard.learnedToday")}
          </div>
        </Link>
        {onStartDueReview ? (
          <div
            role={dueCount > 0 ? "button" : undefined}
            tabIndex={dueCount > 0 ? 0 : undefined}
            onClick={dueCount > 0 ? onStartDueReview : undefined}
            onKeyDown={
              dueCount > 0
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onStartDueReview();
                    }
                  }
                : undefined
            }
            className={`${statCardClass} ${dueCount > 0 ? "" : "opacity-70 cursor-default hover:shadow-sm hover:translate-y-0"}`}
          >
            <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: dueCount > 0 ? "hsl(var(--accent2))" : undefined }}>
              {dueCount}
            </div>
            <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
              <CalendarCheck className="w-4 h-4 text-purple-500 shrink-0" />
              {t("dashboard.reviewToday")}
            </div>
          </div>
        ) : null}
        {isAdminMode && (
          <>
            <div className={`${statCardClass} cursor-default`}>
              <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--primary))" }}>
                {totalUsers.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                <Users className="w-4 h-4 text-indigo-500 shrink-0" />
                {t("admin.total_users", "Total users")}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/support-logs")}
              className={`${statCardClass} text-left transition-all hover:border-[hsl(var(--primary))] hover:shadow-md hover:-translate-y-1`}
              aria-label={t("admin.supportLogs.title")}
            >
              <div className="text-3xl font-bold text-gray-900 dark:text-[hsl(var(--foreground))]" style={{ color: "hsl(var(--primary))" }}>
                {totalAiSupportLogs.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-fuchsia-500 shrink-0" />
                {t("admin.supportLogs.title")}
              </div>
              <div className="text-xs text-gray-500 dark:text-[hsl(var(--muted-foreground))]">
                {t("admin.supportLogs.subtitle")}
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/feedback")}
              className={`${statCardClass} text-left transition-all hover:border-[hsl(var(--primary))] hover:shadow-md hover:-translate-y-1`}
              aria-label={t("admin.feedback.manageFeedback")}
            >
              <div className="flex items-center gap-2 text-2xl font-bold text-[hsl(var(--primary))]">
                <MessageSquare className="w-6 h-6 shrink-0" />
                {t("admin.feedback.manageFeedback")}
              </div>
              <div className="text-sm font-medium text-gray-500 dark:text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                {t("admin.feedback.subtitle")}
              </div>
            </button>
          </>
        )}
      </div>

      {/* Continue Studying */}
      <div style={{ marginBottom: 32 }}>
        {onStartSmartReview && (
          <button
            type="button"
            onClick={onStartSmartReview}
            className="w-full mb-6 flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[hsl(var(--primary)/.4)] bg-[hsl(var(--primary)/.06)] py-4 px-4 text-[hsl(var(--primary))] font-semibold hover:bg-[hsl(var(--primary)/.12)] transition-colors"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            <Brain className="w-5 h-5 shrink-0" />
            {t("smartReview.startSmartReview")}
          </button>
        )}
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "hsl(var(--foreground))",
            marginBottom: 16,
          }}
        >
          {t("dashboard.continueStudying")}
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
          }}
        >
          {recentDecks.map((deck) => (
            <div
              key={deck.id}
              className="home-deck-card"
              style={cardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgb(212, 212, 212)";
                e.currentTarget.style.boxShadow = "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)";
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "hsl(var(--border))";
                e.currentTarget.style.boxShadow = "0 1px 2px 0 rgb(0 0 0 / 0.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
                }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))] shrink-0">
                  <BookOpen size={20} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="text-base font-bold text-[hsl(var(--foreground))] leading-snug" style={{ wordBreak: "break-word" }}>
                    {deck.title}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {t("sidebar.cardsCount", { count: deck.cardCount ?? deck.cards?.length ?? 0 })}
                  </div>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 self-start rounded-lg bg-[hsl(var(--bg3))] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                <Globe size={11} />
                {deck.lang.toUpperCase()}
                {deck.level && ` · ${deck.level}`}
              </div>
              <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                <button
                  onClick={() => handleContinue(deck)}
                  className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-2xl border-0 bg-[hsl(var(--primary))] text-white text-sm font-semibold shadow-sm cursor-pointer hover:shadow-md hover:brightness-110 transition-all duration-150 active:scale-[0.98]"
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  <Play size={14} fill="currentColor" />
                  {t("dashboard.continue")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rank Progress Widget — hidden on mobile (available in Achievements tab); desktop only */}
      <div className="hidden md:block">
        <RankProgressWidget xp={xp} compact />
      </div>
    </div>
  );
}
